import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { Modal, Spin } from 'antd';

import { reorderSequences } from 'leash/utilities/packages/packages.utilities';
import { sequenceSlugByType, SequenceTypesIds, TravelPlan, TravelSequence } from 'types';
import { DetailsLoadingWrapper } from '../Details.styles';
import { useDetailsState } from '../useDetailsState';
import {
  AddSequence,
  DeleteArea,
  DeleteZone,
  DragArea,
  Enumerator,
  EnumeratorItem,
  Sequence,
  SequencesContainer,
  Title,
  Wrapper,
} from './Sequences.styles';
import { FormSequence } from './FormSequence';
import {
  onReorderOrDeleteTravelSequence,
  removeSequences,
} from './FormSequence.utilities';
import { testId } from 'leash/utilities/test/dataTestId';
import { isFinalized } from '../utilities/details.utilities';

const { confirm } = Modal;

interface Props {
  fetchPackage: () => void;
  onSequencesChanges: (sequences: TravelSequence[]) => void;
}

export function Sequences({ fetchPackage, onSequencesChanges }: Props) {
  const [sequencesState, setSequences] = useState<TravelSequence[]>([]);
  const [sequencesLoading, setSequencesLoading] = useState(false);
  const { state, dispatch } = useDetailsState();
  const travelPlan = state?.travelPlan as TravelPlan;
  const workflowStatus = travelPlan?.travelPackage?.workflowStatus;
  const travelProposal = travelPlan?.travelPackage?.travelProposal;
  const emptySequence = {
    travelSequenceId: null,
    travelSequenceTypeSlug: '',
  };

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    setSequencesLoading(true);
    const orderedSequences = reorderSequences(
      sequencesState,
      result.source.index,
      result.destination.index,
    );
    setSequences(orderedSequences);
    const sequence = sequencesState[result.source.index];
    if (sequence?.travelSequenceId && !sequence.isNew) {
      await onReorderOrDeleteTravelSequence(
        sequence?.travelSequenceId as number,
        travelPlan,
        result.destination.index + 1,
      );
    }
    setSequencesLoading(false);
  };

  const onDelete = async (index: number) => {
    const sequencesCloned = [...sequencesState];
    const sequence = sequencesState[index];
    sequence.isDeleting = true;

    setSequences(sequencesCloned);
    confirm({
      title: `Are you sure you want to delete this sequence?`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      onOk: async () => {
        setSequencesLoading(true);
        if (sequence?.travelSequenceId && !sequence.isNew) {
          await onReorderOrDeleteTravelSequence(
            sequence?.travelSequenceId as number,
            travelPlan,
            undefined,
            true,
          );
        }
        const sequencesClonedConfirmed = [...sequencesState];
        const sequenceConfirmed = sequencesClonedConfirmed[index];
        sequenceConfirmed.isDeleting = false;
        sequencesClonedConfirmed.splice(index, 1);
        setSequences(sequencesClonedConfirmed);
        dispatch({ type: 'SET_SEQUENCES', sequences: sequencesClonedConfirmed });
        setSequencesLoading(false);
        onSequencesChanges(sequencesClonedConfirmed);
      },
      onCancel: () => {
        const sequencesClonedCanceled = [...sequencesState];
        const sequenceCanceled = sequencesClonedCanceled[index];
        sequenceCanceled.isDeleting = false;
        setSequences(sequencesClonedCanceled);
        onSequencesChanges(sequencesClonedCanceled);
      },
    });
  };

  const addNewSequence = () => {
    const sequencesCloned = [
      ...sequencesState,
      { ...emptySequence, travelSequenceId: sequencesState.length, isNew: true },
    ];
    setSequences(sequencesCloned);
    onSequencesChanges(sequencesCloned);
  };

  const onChangeSequenceType = (e: SequenceTypesIds, index: number) => {
    const sequencesCloned = [...sequencesState];
    const changedSequence = sequencesCloned[index];
    changedSequence.travelSequenceTypeSlug = sequenceSlugByType[e];
    setSequences(sequencesCloned);
  };

  useEffect(() => {
    setSequences(state.sequences || []);
  }, [state.sequences]);

  useEffect(() => {
    const remove = async (sequences: TravelSequence[]) => {
      await removeSequences(
        travelPlan.travelPackage.id,
        sequences.map((s) => Number(s.travelSequenceId)),
      );
      setSequences([]);
    };

    if (!travelProposal?.departureTime && sequencesState.length) {
      remove(sequencesState);
    }
  }, [travelProposal?.departureTime, sequencesState]);

  if (sequencesLoading) {
    return (
      <DetailsLoadingWrapper>
        <Spin size="large" />
      </DetailsLoadingWrapper>
    );
  }

  if (!travelProposal?.departureTime) {
    return null;
  }

  return (
    <SequencesContainer>
      <Title>Sequences</Title>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {sequencesState.map((sequence, index) => (
                <Draggable
                  key={sequence.travelSequenceId}
                  draggableId={`${sequence.travelSequenceId}`}
                  index={index}
                >
                  {(provided) => (
                    <Wrapper
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <Enumerator>
                        <EnumeratorItem>{index + 1}</EnumeratorItem>
                      </Enumerator>
                      <Sequence deleting={sequence.isDeleting || false}>
                        <DragArea>
                          <MenuOutlined />
                        </DragArea>
                        <FormSequence
                          onChangeSequenceType={(e: SequenceTypesIds) =>
                            onChangeSequenceType(e, index)
                          }
                          sequence={sequence}
                          index={index}
                          fetchPackage={fetchPackage}
                        ></FormSequence>
                        {!isFinalized(workflowStatus) && (
                          <DeleteArea>
                            <DeleteZone onClick={() => onDelete(index)} type="text">
                              <DeleteOutlined />
                            </DeleteZone>
                          </DeleteArea>
                        )}
                      </Sequence>
                    </Wrapper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <AddSequence
        data-testid={testId.addNewSequenceButton}
        type="dashed"
        size="large"
        onClick={addNewSequence}
      >
        + Add a New Sequence
      </AddSequence>
    </SequencesContainer>
  );
}
